/**
 * Canonical WhatsApp inbound message-type registry.
 *
 * Meta ships no chat-UI SDK for Cloud API, so every renderer (chat bubble,
 * sidebar preview, automation previews) has to agree on what a given webhook
 * `type` *means*. This module is that single source of truth, shared by
 * `apps/api` (which stamps `WhatsAppMessageLog.messageType` / `message` at
 * ingest) and `apps/web` (which renders it).
 *
 * Two vocabularies live here:
 *
 *  - `WA_TYPE_LABELS` — real Meta types we receive content for.
 *  - `WA_UNSUPPORTED_LABELS` — the `messages[].unsupported.type` values Meta
 *    sends alongside `type: "unsupported"`. For these the webhook carries NO
 *    content (no text, no media id) — only the kind. Naming the kind is the
 *    most we can ever show.
 *
 * Meta reference: WhatsApp Cloud API -> Webhooks -> messages -> unsupported.
 */
/**
 * Prefix used to encode the underlying kind into the free-form `messageType`
 * column, e.g. `unsupported:poll_creation`. `messageType` is `String?` with no
 * enum, so this needs no migration.
 */
export const WA_UNSUPPORTED_PREFIX = 'unsupported:';
/** Meta error code: "Message type unknown" — Cloud API cannot represent it. */
export const WA_ERR_UNSUPPORTED_TYPE = 131051;
/**
 * Meta error code: "This message is currently unavailable" — fires for
 * messages that predate a Coexistence number being connected.
 */
export const WA_ERR_MESSAGE_UNAVAILABLE = 131060;
/**
 * Every value Meta documents for `messages[].unsupported.type`.
 * Unknown keys fall back to a generic label.
 */
export const WA_UNSUPPORTED_LABELS = {
    button: { icon: '🔘', label: 'Button message' },
    edit: { icon: '✏️', label: 'Edited a message' },
    errors: { icon: '⚠️', label: 'Undeliverable message' },
    gif: { icon: '🎞️', label: 'GIF' },
    group_invite: { icon: '👥', label: 'Group invite' },
    hsm: { icon: '📄', label: 'Template message' },
    image: { icon: '📷', label: 'Photo' },
    interactive: { icon: '🔘', label: 'Interactive message' },
    keep_in_chat: { icon: '📌', label: 'Kept a view-once message' },
    link_preview: { icon: '🔗', label: 'Link' },
    list: { icon: '📋', label: 'List message' },
    location: { icon: '📍', label: 'Location' },
    media_placeholder: { icon: '⏳', label: 'Media still uploading' },
    order: { icon: '🛒', label: 'Order' },
    pin: { icon: '📌', label: 'Pinned a message' },
    poll_creation: { icon: '🗳️', label: 'Poll' },
    poll_update: { icon: '🗳️', label: 'Poll vote' },
    product: { icon: '🛍️', label: 'Product' },
    reaction: { icon: '💬', label: 'Reaction' },
};
/**
 * Kinds whose content genuinely still exists on the handset, so pointing the
 * agent at the WhatsApp app is useful advice. `media_placeholder` is excluded
 * (nothing to see yet — Meta re-delivers once the upload finishes), as is
 * anything reported under {@link WA_ERR_MESSAGE_UNAVAILABLE}.
 */
const HANDSET_VIEWABLE = new Set([
    'button',
    'edit',
    'gif',
    'group_invite',
    'hsm',
    'image',
    'interactive',
    'keep_in_chat',
    'link_preview',
    'list',
    'location',
    'order',
    'pin',
    'poll_creation',
    'poll_update',
    'product',
    'reaction',
]);
/** Real (content-bearing) message types, as stamped by the webhook handler. */
export const WA_TYPE_LABELS = {
    image: { icon: '📷', label: 'Photo' },
    video: { icon: '🎬', label: 'Video' },
    audio: { icon: '🎵', label: 'Audio' },
    voice: { icon: '🎤', label: 'Voice message' },
    document: { icon: '📄', label: 'Document' },
    sticker: { icon: '🧩', label: 'Sticker' },
    location: { icon: '📍', label: 'Location' },
    address: { icon: '🏠', label: 'Address' },
    contacts: { icon: '👤', label: 'Contact card' },
    order: { icon: '🛒', label: 'Order' },
    product: { icon: '🛍️', label: 'Product' },
    product_list: { icon: '🛍️', label: 'Product list' },
    interactive: { icon: '🔘', label: 'Button reply' },
    button: { icon: '🔘', label: 'Button reply' },
    flow: { icon: '📋', label: 'Form' },
    flow_response: { icon: '📋', label: 'Form response' },
    reaction: { icon: '💬', label: 'Reaction' },
    system: { icon: '⚙️', label: 'System update' },
    request_welcome: { icon: '👋', label: 'Requested a conversation' },
    call: { icon: '📞', label: 'Call' },
};
/**
 * Which media player a stored `messageType` maps to. Single source of truth,
 * shared by the chat renderer registry (which player to show), the API DTO
 * (whether a null `storageKey` means "still mirroring" or "not media at all")
 * and the wa-media-mirror worker (which rows to download).
 *
 * `voice` is a distinct type from `audio` — same player, but the label reads
 * "Voice message" — and `sticker` renders in its own chrome-less bubble even
 * though the underlying asset is an image.
 */
export const WA_MEDIA_KIND = {
    image: 'image',
    video: 'video',
    audio: 'audio',
    voice: 'audio',
    document: 'file',
    sticker: 'image',
};
/** Every `messageType` that carries a downloadable Meta media asset. */
export const WA_MEDIA_TYPES = Object.keys(WA_MEDIA_KIND);
/** The player kind for a stored `messageType`, or null when it isn't media. */
export function waMediaKind(messageType) {
    if (!messageType)
        return null;
    return WA_MEDIA_KIND[messageType] ?? null;
}
/** `{ icon, label }` -> `"icon label"`. */
export function formatWaLabel(l) {
    return `${l.icon} ${l.label}`;
}
/**
 * Split a stored `messageType` into its base type and (for unsupported rows)
 * the underlying Meta kind. `"unsupported:poll_creation"` -> base
 * `"unsupported"`, kind `"poll_creation"`.
 */
export function splitWaMessageType(messageType) {
    if (!messageType)
        return { base: null, unsupportedKind: null };
    if (messageType.startsWith(WA_UNSUPPORTED_PREFIX)) {
        return {
            base: 'unsupported',
            unsupportedKind: messageType.slice(WA_UNSUPPORTED_PREFIX.length) || null,
        };
    }
    return { base: messageType, unsupportedKind: null };
}
/** True when `messageType` is an unsupported row, prefixed or bare. */
export function isWaUnsupported(messageType) {
    return splitWaMessageType(messageType).base === 'unsupported';
}
/**
 * Label for an unsupported inbound. `kind` is `messages[].unsupported.type`;
 * `errorCode` is `messages[].errors[0].code`.
 */
export function waUnsupportedLabel(kind, errorCode) {
    if (errorCode === WA_ERR_MESSAGE_UNAVAILABLE) {
        return { icon: '🚫', label: 'Message unavailable' };
    }
    const known = kind ? WA_UNSUPPORTED_LABELS[kind] : undefined;
    return known ?? { icon: '📎', label: 'Unsupported message' };
}
/**
 * Secondary hint shown under the label — where, if anywhere, the agent can
 * actually read this message. Returns null when there is nothing useful to add.
 */
export function waUnsupportedHint(kind, errorCode) {
    if (errorCode === WA_ERR_MESSAGE_UNAVAILABLE) {
        return 'Sent before this number was connected';
    }
    if (kind === 'media_placeholder')
        return 'WhatsApp will resend it shortly';
    if (kind && !HANDSET_VIEWABLE.has(kind))
        return null;
    return 'Open in WhatsApp to view';
}
/**
 * Label for any stored `messageType`, unsupported rows included. Returns null
 * for types that carry their own readable text (`text`, `template`), so
 * callers know to render `message` instead.
 */
export function waTypeLabel(messageType, errorCode) {
    const { base, unsupportedKind } = splitWaMessageType(messageType);
    if (!base)
        return null;
    if (base === 'unsupported')
        return waUnsupportedLabel(unsupportedKind, errorCode);
    if (base === 'text' || base === 'template')
        return null;
    const known = WA_TYPE_LABELS[base];
    if (known)
        return known;
    // Forward-compatibility: a type Meta added after this table was written.
    // Humanise the raw token rather than showing nothing.
    return { icon: '📎', label: humanise(base) };
}
function humanise(token) {
    const words = token.replace(/[_-]+/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
}
/**
 * Legacy-row rescue. Before the registry existed, unsupported inbounds stored
 * Meta's raw error string ("Message type unknown — Message type unknown…") in
 * `message`, which leaked into the sidebar. Detects that shape so callers can
 * substitute a proper label instead of backfilling the table.
 */
export function isLegacyUnsupportedText(message) {
    if (!message)
        return false;
    return /^(message type unknown|unsupported message type|this message is currently unavailable)/i.test(message.trim());
}
/**
 * Pull `unsupported.type` out of a stored raw webhook payload
 * (`WhatsAppMessageLog.payloadJson`). Used for rows written before the kind
 * was promoted into `messageType`, so no backfill is needed.
 */
export function unsupportedKindFromPayload(payloadJson) {
    if (!payloadJson)
        return null;
    try {
        const parsed = JSON.parse(payloadJson);
        return parsed.unsupported?.type ?? null;
    }
    catch {
        return null;
    }
}
